import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'prettyjson',
  pure: true,
  standalone: true,
})
export class PrettyJsonPipe implements PipeTransform {
  transform(value: any, args?: any[]): string {
    const showNumbers = !!args?.[0];
    const padding = this.toInt(args?.[1], 3);

    const findQuery = (args?.[2] ?? '').toString();
    const activeIndex = this.toInt(args?.[3], 0);

    try {
      const obj = typeof value === 'object' ? value : JSON.parse((value ?? '').toString());
      return this.render(obj, showNumbers, padding, findQuery, activeIndex);
    } catch {
      return this.render({ error: 'Invalid JSON' }, showNumbers, padding, findQuery, activeIndex);
    }
  }

  private render(obj: any, showNumbers: boolean, padding: number, findQuery: string, activeIndex: number): string {
    const json = typeof obj === 'string' ? obj : JSON.stringify(obj, null, padding);
    const escaped = this.escapeHtml(json);

    // highlight values + keys
    let highlighted = escaped.replace(
      /("(\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match: string) => {
        let cls = 'number';
        if (match.startsWith('"')) cls = match.endsWith(':') ? 'key' : 'string';
        else if (match === 'true' || match === 'false') cls = 'boolean';
        else if (match === 'null') cls = 'null';
        return `<span class="${cls}">${match}</span>`;
      }
    );

    // Apply search highlight AFTER token highlighting, but only in text parts (not inside tags)
    highlighted = this.applyFindHighlight(highlighted, findQuery, activeIndex);

    if (!showNumbers) return highlighted;

    // Build line-number column separately so it won't be selected
    const lines = highlighted.split('\n');
    const width = String(lines.length).length;
    const nums = lines.map((_, i) => String(i + 1).padStart(width, ' ')).join('\n');

    return `
<div class="pjv-lnwrap">
  <pre class="pjv-ln" aria-hidden="true">${nums}</pre>
  <pre class="pjv-code">${lines.join('\n')}</pre>
</div>`.trim();
  }

  private applyFindHighlight(html: string, query: string, activeIndex: number): string {
    const q = (query ?? '').toString();
    if (!q.trim()) return html;

    // IMPORTANT:
    // - html is already escaped (from escapeHtml(json))
    // - the text we see in the output is escaped, so match on escaped query
    const escapedNeedle = this.escapeHtml(q);
    if (!escapedNeedle.trim()) return html;

    const rx = new RegExp(this.escapeRegExp(escapedNeedle), 'gi');

    let matchIndex = 0;
    const parts = html.split(/(<[^>]+>)/g);

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!part || part.startsWith('<')) continue;

      parts[i] = part.replace(rx, (m) => {
        const isActive = matchIndex === activeIndex;
        const cls = isActive ? 'it-mark is-active' : 'it-mark';

        // IMPORTANT: include BOTH id and data-mi so the component can query reliably
        const out = `<mark class="${cls}" id="it-mi-${matchIndex}" data-mi="${matchIndex}">${m}</mark>`;
        matchIndex++;
        return out;
      });
    }

    return parts.join('');
  }

  private escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private escapeHtml(s: string): string {
    return (s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private toInt(v: any, fallback: number): number {
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : fallback;
  }
}
